/**
 * El puente entre «una lectura salió denegada» y AuthContext.
 *
 * ── El agujero ──────────────────────────────────────────────────────────────
 *
 * Cuando gotrue se queda sin sesión guardada, supabase-js NO falla: manda cada
 * petición con la llave publicable en vez del JWT (`fetchWithAuth`:
 * `accessToken ?? supabaseKey`). El servidor contesta 401 / «permission denied
 * for function get_my_workspace_ids», y la app, que sigue con `isAuthorized`
 * en `true` y el correo en pantalla, no se entera de nada. Reintenta al volver
 * el foco, al reconectar el canal en vivo, al montar cada vista. Un solo
 * navegador dejó 912 informes así entre el 24 y el 30 de agosto de 2026.
 *
 * ── Por qué un puente y no un import ────────────────────────────────────────
 *
 * Quien ve la denegación es `services/vigilarLecturas` (el `onError` del
 * QueryCache, fuera de React) y `WorkspaceContext` (lista de talleres vacía).
 * Quien puede decidir es `AuthContext`, que es el único que sabe si hay token
 * guardado y el único que puede cerrar la sesión local. Este módulo no importa
 * nada: registra un oyente y lo llama. Así `vigilarLecturas` sigue sin tocar
 * `supabase`, y sus pruebas siguen sin doblarlo.
 */

export type OyenteDeDenegacion = (origen: string, motivo: string) => void;

let oyente: OyenteDeDenegacion | null = null;

/** AuthContext se registra al montar y se borra al desmontar. */
export function alDenegarLectura(fn: OyenteDeDenegacion | null): void {
    oyente = fn;
}

/** Una lectura salió denegada. `origen` dice cuál (p. ej. `lectura:estimates`). */
export function notificarLecturaDenegada(origen: string, motivo: string): void {
    oyente?.(origen, motivo);
}

/**
 * ¿Es este error un «no tienes permiso» o un «tu credencial no vale»?
 *
 * Se miran las tres caras que tiene: el código de Postgres (42501), los de
 * PostgREST para el JWT (PGRST301 inválido, PGRST303 caducado), un 401 seco y
 * el texto, porque `database.ts` envuelve muchos errores en un `Error` nuevo
 * con solo el mensaje. Un fallo de red o un 500 NO cuentan: no dicen nada de la
 * sesión.
 */
export function esDenegacion(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const e = error as { code?: unknown; status?: unknown; message?: unknown };
    const code = typeof e.code === 'string' ? e.code : '';
    const status = typeof e.status === 'number' ? e.status : 0;
    const mensaje = typeof e.message === 'string' ? e.message : '';
    return code === '42501'
        || code === 'PGRST301'
        || code === 'PGRST303'
        || status === 401
        || /permission denied for (function|table|schema|relation)/i.test(mensaje)
        || /\bJWT (expired|invalid|malformed)/i.test(mensaje);
}
